import React, { useState, useEffect } from 'react';
import { Typography, Box, ButtonGroup, Button, Modal, IconButton, Fade, Paper, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { tooltipClasses } from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import { DropZone, TooltipCustom } from 'components/common';
import Editor from '@monaco-editor/react';
import { useTheme } from '@mui/material/styles';
import { Controller } from "react-hook-form";
import { generateGuid } from 'helpers';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined';
import DoDisturbAltOutlinedIcon from '@mui/icons-material/DoDisturbAltOutlined';
import { makeStyles } from '@mui/styles';
import YAML from 'js-yaml';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';


const useStyle = makeStyles((theme) => ({
  style: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    gap: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3),
  },
}));

const ToolTipStyle = styled(({ className, ...props }) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.primary.light,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    borderRadius: '4px',
    backgroundColor: theme.palette.primary.light,
  },
}));

export default function FormMonacoEditor(props) {
  const classes = useStyle();
  const {
    id,
    name,
    label,
    height="400px",
    width="100%",
    language='json',
    languageOptions=[],
    options = {},
    helperText,
    methods,
    handleSetLanguage,
    maximize = true,
    acceptedFormats = ['txt', 'json', 'yml','ini'],
    uploadEnable = true,
    downloadEnable = false,
    handleDownload = ()=>{}
  } = props;
  const { control, getValues, setValue } = methods;
  const [t, i18n] = useTranslation();
  const theme = useTheme();
  const [countLines, setCountLines] = useState(0);
  const [ showDropZone, setShowDropZone ] = useState( false );
  const [acceptedFormatFile, setAcceptedFormatFile] = useState(acceptedFormats);
  const defaultId = !id ?? ('editor-text-' + generateGuid());
  const optionDefault = {
    selectOnLineNumbers: true,
    autoIndent: 'full',
    wordWrapColumn: 80,
    formatOnPaste: true,
    mouseWheelZoom: true,
    minimap: { enabled: false },
    cursorStyle: 'block',
    cursorSmoothCaretAnimation: true,
    readOnly: false,
    ...options
  };
  const [isMaximize, setIsMaximize] = useState(false);

  const [languageSelected, setLanguageSelected] = useState(language);

  const handleCountLines = (editorText) => {
    if (!editorText) return 0;
    setCountLines(editorText.split(/\r\n|\r|\n/).length);
  };

  const handleDrop = (fileContents) => {
    setValue(name, fileContents);
    setShowDropZone(false);
  }

  useEffect(() => {
    handleCountLines(getValues(name));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleChangeLanguage(language);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  const changeMonacoValue = (previousLanguage, languageToChange) => {
    if (previousLanguage === 'yaml' && languageToChange === 'json') {
      const yaml = YAML.load(getValues(name));
      yaml && setValue(name, JSON.stringify(yaml, null, 2));
      return;
    }

    if (previousLanguage === 'json' && languageToChange === 'yaml') {
      try {
        const json = JSON.parse(getValues(name));
        json && setValue(name, YAML.dump(json, { indent: 2,}));
        return;
      } catch (e) {
          return;
      }
    }
  }

  const handleChangeLanguage = (language) => {
    const previousLanguage = languageSelected;
    const languageToChange = language;
    handleAcceptedFormats(language);
    setLanguageSelected(language);

    if (handleSetLanguage) {
      handleSetLanguage(language);
    }
    changeMonacoValue(previousLanguage, languageToChange);
  }

  const handleAcceptedFormats = (language) => {
    if (language === 'json') {
      setAcceptedFormatFile(['json']);
    } else if (language === 'yaml') {
      setAcceptedFormatFile(['yaml']);
    } else {
      setAcceptedFormatFile(['txt','ini']);
    }
  }


  const languageButtons = languageOptions.map(l => (
    <Button
      onClick={() => handleChangeLanguage(l)}
      sx={{
        borderRadius: 0.5,
        bgcolor: (l === languageSelected) ? theme.palette.primary[200] : 'transparent'
      }}
      key={`${l}-button`}
    >
      {l.toUpperCase()}
    </Button>
  ));



  const MonacoEditor = (
    <Box
      sx={{
        display: 'flex',
        gap: 0.5,
        flexDirection: 'column',
        alignItems: 'flex-start',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 1,
          alignItems: 'center',
          mb: 1,
          mt: 1,
        }}
      >
        <Typography variant="subtitle1">
          {i18n.exists(label) ? t(label) : label}
        </Typography>
        {helperText && <TooltipCustom title={helperText} />}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          border: `1px solid ${theme.palette.grey[400]}`,
          borderRadius: 0.5,
          width: '100%',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: theme.palette.grey[100],
            borderRadius: '12px 12px 0px 0px',
            borderBottom: `1px solid ${theme.palette.grey[400]}`,
            marginBottom: '8px',
            padding: '8px 16px',
            width: '100%',
          }}
        >
          <Typography
            variant="caption"
            sx={{ fontFamily: 'monospace', flex: '1', justifyContent: 'flex-start' }}
          >
            {t('templateList.form.variables.subtitle', { count: countLines, language: languageSelected.toUpperCase() })}
          </Typography>

          { languageOptions.length > 0 &&
            <ButtonGroup size='small' arial-label='language-buttons'>
              { languageButtons }
            </ButtonGroup>
          }

          { showDropZone &&
            <ToolTipStyle
              arrow
              disableFocusListener
              placement="top"
              title={t('common.components.cancelImportFile')}
              TransitionComponent={Fade}
              TransitionProps={{ timeout: 300 }}
            >
              <IconButton
                sx={{
                  padding: 0
                }}
                aria-label="cancel import file"
                size="small"
                onClick= { () => setShowDropZone(false)}
              >
                <DoDisturbAltOutlinedIcon fontSize="small" />
              </IconButton>
            </ToolTipStyle>


          }

          { !showDropZone &&
            <>


              { uploadEnable && <ToolTipStyle
                arrow
                disableFocusListener
                placement="top"
                title={t('common.components.importFile')}
                TransitionComponent={Fade}
                TransitionProps={{ timeout: 300 }}
              >
                <IconButton
                  sx={{
                    padding: 0
                  }}
                  aria-label="import file"
                  size="small"
                  onClick= { () => setShowDropZone(true)}
                >
                  <PublishOutlinedIcon fontSize="small" />
                </IconButton>
              </ToolTipStyle>}

              { downloadEnable && <ToolTipStyle
                arrow
                disableFocusListener
                placement="top"
                title={t('common.components.exportFile')}
                TransitionComponent={Fade}
                TransitionProps={{ timeout: 300 }}
              >
                <IconButton
                  sx={{
                    padding: 0
                  }}
                  aria-label="Download file"
                  size="small"
                  onClick= { () => handleDownload()}
                >
                  <FileDownloadOutlinedIcon fontSize="small" />
                </IconButton>
              </ToolTipStyle>}

              { maximize &&
                <ToolTipStyle
                  arrow
                  disableFocusListener
                  placement="top"
                  title={t('common.components.maximizeLabel')}
                  TransitionComponent={Fade}
                  TransitionProps={{ timeout: 300 }}
                >
                  <IconButton
                    sx={{
                      padding: 0
                    }}
                    aria-label="maximize"
                    size="small"
                    onClick= { () => setIsMaximize(!isMaximize)}
                  >
                    <CloseFullscreenIcon fontSize="small" />
                  </IconButton>
                </ToolTipStyle>
              }
            </>
          }
        </Box>

        {!showDropZone &&
          <Controller
            control={control}
            name={name}
            id={defaultId}

            render={({ field: { onChange, value } }) => (
              <Editor
                id='editor-text'
                height={isMaximize ? '80vh' : height}
                width = {isMaximize ? '90vw' : width}
                language={languageSelected}
                options={optionDefault}
                value={value}
                onChange={(e) => {
                  handleCountLines(e);
                  return onChange(e);
                }}
              />
            )}
          />
        }
        {showDropZone &&
          <DropZone onDrop={handleDrop} accept={acceptedFormatFile} />
        }
      </Box>
    </Box>
  );

  return (
    <>
      {MonacoEditor}

      <Modal
        sx={{
          backdropFilter: 'blur(2px)'
        }}
        open={isMaximize}
        onClose={ () => setIsMaximize(false) }
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Fade in={isMaximize}>
          <Paper className={classes.style}>
            {MonacoEditor}
          </Paper>
        </Fade>
      </Modal>
    </>
  );
}
